import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight'
import { faLayerGroup } from '@fortawesome/pro-regular-svg-icons/faLayerGroup'
import { faWallet } from '@fortawesome/pro-regular-svg-icons/faWallet'
import { Box, LoadingOverlay, Flex, Text, Anchor } from '@mantine/core'
import clsx from 'clsx'
import Link from 'next/link'
import React from 'react'

import {
  useQueryGetAllBookingInsurances,
  useQueryGetAllBookingInsurancesQuote,
} from '@flecto/core/saas'
import { useTranslation, Trans } from '@flecto/translations'
import { Badge, Icon, iconInsuranceOn } from '@flecto/ui'
import Card from '@flecto/ui/lib/ui/atoms/Card'
import Switch from '@flecto/ui/lib/ui/atoms/Switch'
import { valueFromCents } from '@flecto/ui/lib/utils/Number'

import { useInsuranceManagerContext } from '../../../provider'
import { EKindTab, useInsuranceManagerState } from '../../../state'
import classes from '../InsuranceManager.module.css'
import { Body } from './Body'

export default function TabGeneral() {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_sidebar' })
  const { setTab } = useInsuranceManagerState()
  const { booking, toggleInsurance, hasInsurance } =
    useInsuranceManagerContext()
  const data = booking?.data

  const bookingId = String(booking.data?.internal_id)

  const { data: insuranceQuoteData, refetch } =
    useQueryGetAllBookingInsurancesQuote({
      bookingId,
      bookingItems: [],
    })

  const { data: insurancesInfo, isLoading } = useQueryGetAllBookingInsurances({
    bookingId,
  })

  React.useEffect(() => {
    if (booking.data?.internal_id) {
      refetch()
    }
  }, [])

  const insured = data?.insured ?? false
  const canShowBadge =
    !isLoading && !insured && !hasInsurance && insuranceQuoteData?.total_cents

  return (
    <>
      <LoadingOverlay visible={toggleInsurance.loading || booking.loading} />
      <Box p='md' className={classes.topCard}>
        <Card className={clsx({ [classes.cardChecked]: insured })}>
          <Flex justify='space-between' align='center'>
            <Flex align='center' gap={16}>
              <Icon icon={iconInsuranceOn} color='brand' size='md' />
              <Flex direction='column'>
                <Flex align='center'>
                  <Text fw={500} display='inline-flex'>
                    {t('insurances_modal.active_insurance.title')}
                  </Text>
                  {Boolean(canShowBadge) && (
                    <Box ml='sm'>
                      <Badge variant='light' color='blue'>
                        {t(
                          'insurances_modal.active_insurance.estimate_cost_quote',
                          {
                            value: valueFromCents(
                              insuranceQuoteData?.total_cents ?? 0
                            ).currency,
                          }
                        )}

                        <Icon
                          icon={faInfoCircle}
                          color='blue'
                          ml='xs'
                          size='xs'
                        />
                      </Badge>
                    </Box>
                  )}
                </Flex>
                <Text>
                  {t('insurances_modal.active_insurance.description')}
                </Text>
              </Flex>
            </Flex>
            <Switch checked={insured} onChange={toggleInsurance.toggle} />
          </Flex>
        </Card>
      </Box>

      <Body>
        <Card
          disabled={!insured && !hasInsurance}
          onClick={() => setTab(EKindTab.ISSUES)}
          style={{ cursor: 'pointer' }}
        >
          <Flex justify='space-between' align='center'>
            <Flex align='center' gap={16}>
              <Icon icon={faLayerGroup} size='md' />
              <Flex direction='column' align='flex-start'>
                <Text fw={500}>{t('insurances_modal.issues.title')}</Text>
                <Text>{t('insurances_modal.issues.description')}</Text>
              </Flex>
            </Flex>
            <Icon icon={faChevronRight} />
          </Flex>
        </Card>

        <Box>
          <Card disabled={!insured && !hasInsurance}>
            <Flex justify='space-between' align='center'>
              <Flex align='center' gap={16}>
                <Icon icon={faWallet} size='md' />
                <Flex direction='column'>
                  <Text fw={500}>{t('insurances_modal.total_cost.title')}</Text>
                  <Text>
                    <Trans
                      ns='saas'
                      i18nKey={
                        'booking_sidebar.insurances_modal.total_cost.description'
                      }
                      components={{
                        a: <Link href='/profile/billing' />,
                        b: <Anchor />,
                      }}
                    />
                  </Text>
                </Flex>
              </Flex>
              <Text>
                {valueFromCents(insurancesInfo?.total_cents ?? 0).currency}
              </Text>
            </Flex>
          </Card>
        </Box>
      </Body>
    </>
  )
}
